import moment, { Moment } from "moment";

import {
  FilterDefinitionDto,
  FilterOperator,
  FilterType,
  FilterValueType,
} from "@/core/api/generated/v1";

import { FilterDefinition } from "../filters/filterDefinition";
import { FilterDefinitionItem } from "../filters/filterDefinitionItem";
import { FilterFieldOperatorSpec } from "../filters/filterFieldOperatorSpec";
import { FilterSpec } from "../filters/filterSpec";
import { ApiEnumName, ApiEnumValue, enumService } from "../services/enum";
import { FilterValue, FilterValueDate } from "../ts/filters";
import { TypeHelper } from "./type";

export class FilterHelper {
  public static getOperatorDisplayName(operator: FilterOperator): string {
    return (
      (operator === FilterOperator.Equal ? "Equals" : undefined) ??
      (operator === FilterOperator.NotEqual ? "Does not equal" : undefined) ??
      (operator === FilterOperator.Contain ? "Contains" : undefined) ??
      (operator === FilterOperator.NotContain ? "Does not contain" : undefined) ??
      (operator === FilterOperator.LessThan ? "Less than" : undefined) ??
      (operator === FilterOperator.LessThanOrEqual ? "Less than or equals" : undefined) ??
      (operator === FilterOperator.GreaterThan ? "Greater than" : undefined) ??
      (operator === FilterOperator.GreaterThanOrEqual ? "Greater than or equals" : undefined) ??
      (operator === FilterOperator.AnyOf ? "Any of" : undefined) ??
      (operator === FilterOperator.NoneOf ? "None of" : undefined) ??
      (operator === FilterOperator.Empty ? "Is empty" : undefined) ??
      (operator === FilterOperator.NotEmpty ? "Is not empty" : undefined) ??
      enumService.getEnumValueName("FilterOperator", operator)
    );
  }

  /** Fixes filter definition to correspond filter spec.
   * Removes filters that don't correspond the spec (e.g. fields that are missing in the spec).
   */
  public static fixFilterToCorrespondSpec({
    spec,
    definition,
  }: {
    spec: FilterSpec;
    definition: FilterDefinition;
  }): {
    isFixed: boolean;
    newDefinition: FilterDefinition;
    removedItems?: FilterDefinitionItem[];
  } {
    let isFixed = false;
    let newDefinition = definition;
    const removedItems: FilterDefinitionItem[] = [];

    const itemsWithoutSpec = definition.items?.filter((item) => !spec.hasField(item.field)) ?? [];
    if (!TypeHelper.isEmpty(itemsWithoutSpec)) {
      newDefinition = new FilterDefinition(definition);
      itemsWithoutSpec.forEach((item) => {
        newDefinition.removeItem(item);
        removedItems.push(item);
      });
      isFixed = true;
    }

    return { isFixed, newDefinition, removedItems };
  }

  // #region Value

  /** Converts filter value to valid format according to the value type. */
  public static formatValue(
    operatorSpec: FilterFieldOperatorSpec,
    value: FilterValue,
  ): FilterValue {
    let newValue: FilterValue = undefined;
    switch (operatorSpec.valueType) {
      case FilterValueType.None:
        newValue = undefined;
        break;
      case FilterValueType.Arbitrary:
        newValue = value;
        break;
      case FilterValueType.String:
        newValue = this.valueAsString(value);
        break;
      case FilterValueType.Number:
        newValue = this.valueAsNumber(value);
        break;
      case FilterValueType.Boolean:
        newValue = this.valueAsBoolean(value);
        break;
      case FilterValueType.Date:
        newValue = this.valueAsDate(value);
        break;
      case FilterValueType.Enum:
        newValue = this.valueAsEnum(value);
        break;
      case FilterValueType.Id:
        newValue = this.valueAsId(value);
        break;
      case FilterValueType.ArrayOfString:
        newValue = this.valueAsArrayOfString(value);
        break;
      case FilterValueType.ArrayOfEnum:
        newValue = this.valueAsArrayOfEnum(value);
        break;
      case FilterValueType.ArrayOfId:
        newValue = this.valueAsArrayOfId(value);
        break;
      default:
        throw new Error(
          `Filter value '${value}' is invalid for value type '${operatorSpec.valueType}' specified in the spec.`,
        );
    }
    // console.log("formatValue.", { value, newValue });

    return newValue;
  }

  public static valueAsString(value: FilterValue): string | undefined {
    return TypeHelper.isPrimitive(value) ? value?.toString() : undefined;
  }

  public static valueAsNumber(value: FilterValue): number | undefined {
    return TypeHelper.isPrimitive(value)
      ? TypeHelper.isNumber(value)
        ? value
        : TypeHelper.isString(value)
          ? Number(value)
          : undefined
      : undefined;
  }

  public static valueAsBoolean(value: FilterValue): boolean | undefined {
    return TypeHelper.isPrimitive(value)
      ? TypeHelper.isBoolean(value)
        ? value
        : TypeHelper.isString(value)
          ? TypeHelper.parseBoolean(value)
          : undefined
      : undefined;
  }

  public static valueAsDate(value: FilterValue): FilterValueDate | undefined {
    return TypeHelper.isPrimitive(value)
      ? TypeHelper.isString(value)
        ? value
        : undefined
      : TypeHelper.isMoment(value)
        ? value
        : TypeHelper.isDate(value)
          ? value
          : undefined;
  }

  public static valueAsDateAsMoment(value: FilterValue): Moment | undefined {
    return TypeHelper.isPrimitive(value)
      ? TypeHelper.isString(value)
        ? moment(value)
        : undefined
      : TypeHelper.isMoment(value)
        ? value
        : TypeHelper.isDate(value)
          ? moment(value)
          : undefined;
  }

  public static valueAsDateAsString(value: FilterValue): string | undefined {
    return TypeHelper.isPrimitive(value)
      ? TypeHelper.isString(value)
        ? value
        : undefined
      : TypeHelper.isMoment(value)
        ? value.format()
        : TypeHelper.isDate(value)
          ? moment(value).format()
          : undefined;
  }

  public static valueAsId(value: FilterValue): string | undefined {
    return this.valueAsString(value);
  }

  public static valueAsEnum(value: FilterValue): string | undefined {
    return this.valueAsString(value);
  }

  public static valueAsEnumTyped<TEnumName extends ApiEnumName>(
    enumName: TEnumName,
    value: FilterValue,
  ): ApiEnumValue<TEnumName> | undefined {
    const str = this.valueAsString(value);
    if (!str) {
      return undefined;
    }
    const tempEnumValue = str as ApiEnumValue<TEnumName>;
    const enumValues = enumService.getEnumValues(enumName);
    if (!enumValues.includes(tempEnumValue)) {
      return undefined;
    }
    return tempEnumValue;
  }

  public static valueAsArrayOfString(value: FilterValue): string[] | undefined {
    return TypeHelper.isArray(value)
      ? value.map((x) => this.valueAsString(x)!).filter((x) => !TypeHelper.isEmpty(x))
      : undefined;
  }

  public static valueAsArrayOfEnum(value: FilterValue): string[] | undefined {
    return this.valueAsArrayOfString(value);
  }

  public static valueAsArrayOfEnumTyped<TEnumName extends ApiEnumName>(
    enumName: TEnumName,
    value: FilterValue,
  ): Array<ApiEnumValue<TEnumName>> | undefined {
    return TypeHelper.isArray(value)
      ? value.map((x) => this.valueAsEnumTyped(enumName, x)!).filter((x) => !TypeHelper.isEmpty(x))
      : undefined;
  }

  public static valueAsArrayOfId(value: FilterValue): string[] | undefined {
    return this.valueAsArrayOfString(value);
  }

  // #endregion

  //#region Mapping

  public static mapFilterDefinitionToFilterDefinitionDto(
    source: FilterDefinition | undefined,
    options?: { filterType?: FilterType },
  ): FilterDefinitionDto | undefined {
    source?.cleanup();
    if (!source || !source.isValid) {
      return undefined;
    }

    source = source.clone(); // clone to avoid modifying objects in use
    source.formatBeforeJsonSerialization();

    return {
      items: source.items
        .filter((x) => (options?.filterType ? x.type === options.filterType : true))
        .map((x) => ({
          type: x.type,
          field: x.field,
          operator: x.operator,
          valueType: x.valueType,
          value: x.value,
        })),
    };
  }

  public static mapFilterDefinitionDtoToFilterDefinition(
    source: FilterDefinitionDto | undefined,
    options?: { filterType?: FilterType },
  ): FilterDefinition | undefined {
    if (!source?.items) {
      return undefined;
    }

    return new FilterDefinition({
      items: source.items
        .filter((x) => (options?.filterType ? x.type === options.filterType : true))
        .map(
          (x) =>
            (TypeHelper.isAllNotNil([x.type, x.field, x.operator, x.valueType, x.value])
              ? new FilterDefinitionItem({
                  type: x.type!,
                  field: x.field!,
                  operator: x.operator!,
                  valueType: x.valueType!,
                  value: x.value!,
                })
              : undefined)!,
        )
        .filter(Boolean),
    });
  }

  public static serializeFilterDefinitionIntoString(
    source: FilterDefinition | undefined,
    options?: { filterType?: FilterType },
  ): string | undefined {
    source?.cleanup();
    if (!source || !source.isValid) {
      return undefined;
    }

    const dto = this.mapFilterDefinitionToFilterDefinitionDto(source, options);
    const json = JSON.stringify(dto);
    return json;
  }

  //#endregion
}
